import { useEffect, useRef, useState } from "react";

const STEPS = [
  {
    n: "01",
    title: "Llamada introductoria",
    text: "Conversemos sobre tu empresa, tus desafíos y el momento actual de tu equipo.",
  },
  {
    n: "02",
    title: "Reunión de 90 Minutos",
    text: "Una sesión para entender EOS y visualizar cómo podría ayudar a tu empresa y equipo de liderazgo.",
  },
  {
    n: "03",
    title: "Focus Day",
    text: "Primer día de trabajo para instalar herramientas prácticas de foco, accountability, reuniones y datos.",
  },
  {
    n: "04",
    title: "Vision Building",
    text: "Construcción y alineamiento de visión de corto, mediano y largo plazo.",
  },
  {
    n: "05",
    title: "Sesiones trimestrales",
    text: "Ritmo de ejecución, revisión de prioridades y resolución de asuntos relevantes.",
  },
  {
    n: "06",
    title: "Sesión anual",
    text: "Planificación del siguiente año con claridad y foco.",
  },
];

const STAGGER_MS = 220;
const STEP_DURATION_MS = 700;

export default function Process() {
  return (
    <section id="implementacion" data-testid="process-section" className="section-padding bg-white">
      <div className="container-eos">
        <div className="max-w-3xl reveal">
          <span className="eyebrow">El proceso</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
            Un proceso probado para instalar{" "}
            <span className="text-[#F26522]">foco y disciplina</span>.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Una hoja de ruta clara: del primer contacto a un ritmo trimestral consistente.
          </p>
        </div>

        <ProcessCycle />
      </div>
    </section>
  );
}

function ProcessCycle() {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) { setStarted(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { setStarted(true); io.disconnect(); }
      }),
      { threshold: 0.25 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const totalLineDuration = STAGGER_MS * (STEPS.length - 1) + STEP_DURATION_MS;

  return (
    <div ref={ref} className="mt-14">
      {/* Desktop: horizontal cycle with animated connecting line */}
      <div className="hidden lg:block">
        <div className="relative">
          {/* Base line */}
          <div className="absolute left-0 right-0 top-[34px] h-px bg-slate-200" />
          {/* Animated progress line */}
          <div
            className="absolute left-0 top-[34px] h-px bg-[#F26522] origin-left"
            style={{
              transform: started ? "scaleX(1)" : "scaleX(0)",
              width: "100%",
              transition: `transform ${totalLineDuration}ms cubic-bezier(0.65, 0, 0.35, 1)`,
            }}
          />
          <div className="grid grid-cols-6 gap-4">
            {STEPS.map((s, i) => {
              const delay = i * STAGGER_MS;
              return (
                <div
                  key={s.n}
                  data-testid={`process-step-${s.n}`}
                  className="relative"
                  style={{
                    opacity: started ? 1 : 0,
                    transform: started ? "translateY(0) scale(1)" : "translateY(10px) scale(0.94)",
                    transition: `opacity ${STEP_DURATION_MS}ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform ${STEP_DURATION_MS}ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
                  }}
                >
                  <div className="relative z-10 mx-auto w-[68px] h-[68px] rounded-full bg-white border-2 border-[#F26522] flex items-center justify-center font-display font-extrabold text-[#F26522] text-lg shadow-sm">
                    {s.n}
                  </div>
                  <h3 className="mt-5 font-display font-bold text-navy-800 text-base leading-snug text-center">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-snug text-center">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile/Tablet: vertical timeline */}
      <ol className="lg:hidden relative ml-4 space-y-8">
        {/* Base vertical line */}
        <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-slate-200" aria-hidden="true" />
        {/* Animated vertical progress line */}
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 w-[2px] bg-[#F26522] origin-top"
          style={{
            height: "100%",
            transform: started ? "scaleY(1)" : "scaleY(0)",
            transition: `transform ${totalLineDuration}ms cubic-bezier(0.65, 0, 0.35, 1)`,
          }}
        />
        {STEPS.map((s, i) => {
          const delay = i * STAGGER_MS;
          return (
            <li
              key={s.n}
              data-testid={`process-step-mobile-${s.n}`}
              className="pl-6 relative"
              style={{
                opacity: started ? 1 : 0,
                transform: started ? "translateX(0)" : "translateX(-8px)",
                transition: `opacity ${STEP_DURATION_MS}ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform ${STEP_DURATION_MS}ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
              }}
            >
              <span className="absolute -left-[19px] w-9 h-9 rounded-full bg-white border-2 border-[#F26522] flex items-center justify-center font-display font-extrabold text-[#F26522] text-sm">
                {s.n}
              </span>
              <h3 className="font-display font-bold text-navy-800 text-lg">{s.title}</h3>
              <p className="mt-1.5 text-slate-600 leading-relaxed">{s.text}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
